import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { useI18n } from '../i18n';
import { Modal } from './ui';

/** Size of the square the person adjusts in, in CSS pixels. */
const VIEW = 280;
/** Size of the picture that is uploaded. */
const OUTPUT = 384;
const MAX_ZOOM = 3;

/**
 * Lets the person move and zoom a photo inside a circle, then hands back the
 * square crop as a WebP (JPEG where the browser can't encode WebP). Keeping the
 * upload small and square also means the server needs no image processing.
 */
export function AvatarCropper({ file, onCancel, onDone }: { file: File; onCancel: () => void; onDone: (blob: Blob) => void }) {
  const { t } = useI18n();
  const canvas = useRef<HTMLCanvasElement>(null);
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [broken, setBroken] = useState(false);
  const [zoom, setZoom] = useState(1);
  // The centre of the crop, in image pixels.
  const [centre, setCentre] = useState({ x: 0, y: 0 });
  const drag = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    const el = new Image();
    el.onload = () => {
      setImg(el);
      setCentre({ x: el.naturalWidth / 2, y: el.naturalHeight / 2 });
    };
    el.onerror = () => setBroken(true);
    el.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // Pixels of the image per pixel of the view: at zoom 1 the shorter side just fills the circle.
  const scale = img ? (VIEW / Math.min(img.naturalWidth, img.naturalHeight)) * zoom : 1;

  const clamp = useCallback(
    (c: { x: number; y: number }, s: number) => {
      if (!img) return c;
      const half = VIEW / s / 2;
      return {
        x: Math.min(Math.max(c.x, half), Math.max(img.naturalWidth - half, half)),
        y: Math.min(Math.max(c.y, half), Math.max(img.naturalHeight - half, half)),
      };
    },
    [img],
  );

  useEffect(() => {
    if (!img || !canvas.current) return;
    const ctx = canvas.current.getContext('2d');
    if (!ctx) return;
    const c = clamp(centre, scale);
    const side = VIEW / scale;
    ctx.clearRect(0, 0, VIEW, VIEW);
    ctx.drawImage(img, c.x - side / 2, c.y - side / 2, side, side, 0, 0, VIEW, VIEW);
  }, [img, centre, scale, clamp]);

  function move(dx: number, dy: number) {
    setCentre((c) => clamp({ x: c.x - dx / scale, y: c.y - dy / scale }, scale));
  }
  function onPointerMove(e: PointerEvent) {
    if (!drag.current) return;
    move(e.clientX - drag.current.x, e.clientY - drag.current.y);
    drag.current = { x: e.clientX, y: e.clientY };
  }
  function onKeyDown(e: KeyboardEvent) {
    const step = 12;
    const d = { ArrowLeft: [step, 0], ArrowRight: [-step, 0], ArrowUp: [0, step], ArrowDown: [0, -step] }[e.key];
    if (!d) return;
    e.preventDefault();
    move(d[0]!, d[1]!);
  }

  function save() {
    if (!img) return;
    const out = document.createElement('canvas');
    out.width = out.height = OUTPUT;
    const ctx = out.getContext('2d');
    if (!ctx) return onDone(file);
    const c = clamp(centre, scale);
    const side = VIEW / scale;
    ctx.drawImage(img, c.x - side / 2, c.y - side / 2, side, side, 0, 0, OUTPUT, OUTPUT);
    out.toBlob(
      (webp) => {
        if (webp && webp.type === 'image/webp') return onDone(webp);
        out.toBlob((jpeg) => onDone(jpeg ?? file), 'image/jpeg', 0.9);
      },
      'image/webp',
      0.9,
    );
  }

  return (
    <Modal
      title={t('account.crop_title')}
      onClose={onCancel}
      width={380}
      footer={
        <>
          <button type="button" className="btn" onClick={onCancel}>
            {t('common.cancel')}
          </button>
          <button type="button" className="btn primary" disabled={!img} onClick={save}>
            {t('account.crop_use')}
          </button>
        </>
      }
    >
      {broken ? (
        <div className="banner">{t('account.crop_unreadable')}</div>
      ) : (
        <div className="cropper">
          <div className="cropper-stage" style={{ width: VIEW, height: VIEW }}>
            <canvas
              ref={canvas}
              width={VIEW}
              height={VIEW}
              tabIndex={0}
              aria-label={t('account.crop_hint')}
              onPointerDown={(e) => {
                e.currentTarget.setPointerCapture(e.pointerId);
                drag.current = { x: e.clientX, y: e.clientY };
              }}
              onPointerMove={onPointerMove}
              onPointerUp={() => (drag.current = null)}
              onPointerCancel={() => (drag.current = null)}
              onKeyDown={onKeyDown}
            />
            <span className="cropper-guide" aria-hidden />
          </div>
          <label className="cropper-zoom">
            <span>{t('account.crop_zoom')}</span>
            <input type="range" min={1} max={MAX_ZOOM} step={0.01} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} />
          </label>
          <p className="muted cropper-hint">{t('account.crop_hint')}</p>
        </div>
      )}
    </Modal>
  );
}
